import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import { mergeRight, remove } from 'ramda';
import { IconComp } from './icon-list-editor/icon-comp';
import Editor from './icon-list-editor/editor';
import { icon as Icon } from './data/icon';
import { assert, amend } from '../lib/utils';

import * as api from './api';
import setting from '../../setting';

const css = require('./icon-list-editor.css').default;

export default function ({ page }: {
    page: string,
}) {
    const [icons, setIcons] = useState<Icon[]>([]);
    const main = useRef<HTMLDivElement>(null);
    const [editing, setEditing] = useState<number | null>(null);
    const [moving, setMoving] = useState<moving | null>(null);
    type moving = {
        target: number,
        scale: {
            width: number,
            height: number,
        },
        pos: {
            offsetX: number,
            offsetY: number,
        },
    }

    useEffect(() => {
        fetchIcons(page).then(setIcons);
    }, [page]);

    const scale = main.current === null ? null : {
        w: main.current.getBoundingClientRect().width / main.current!.clientWidth,
        h: main.current.getBoundingClientRect().height / main.current!.clientHeight,
    }

    return (
    <div
        id={css.main}
        ref={main}
        style={{ pointerEvents: moving === null ? 'none' : 'auto' }}
        onMouseMove={e => {
            if (moving !== null) {
                const rect = main.current!.getBoundingClientRect();
                const left = ((e.nativeEvent.x - rect.x) * moving.scale.width) - moving.pos.offsetX;
                const top = ((e.nativeEvent.y - rect.y) * moving.scale.height) - moving.pos.offsetY;
                setIcons(amend(
                    icons,
                    moving.target,
                    mergeRight(icons[moving.target], { left, top })));
            }
        }}
        onMouseUp={e => setMoving(null)}
    >
        {(() => {
            return icons.map((icon, idx) => (
                <IconComp
                    icon={icon}
                    style={{
                        position: 'absolute',
                        left: `${icon.left}px`,
                        top: `${icon.top}px`,
                    }}
                    onDoubleClick={() => setEditing(idx)}
                    onMouseDown={e => {
                        assert(e.target !== null, '');
                        const target = e.currentTarget;
                        setMoving({
                            target: idx,
                            scale: {
                                width: target.clientWidth / target.getBoundingClientRect().width,
                                height: target.clientHeight / target.getBoundingClientRect().height,
                            },
                            pos: {
                                offsetX: e.nativeEvent.offsetX,
                                offsetY: e.nativeEvent.offsetY,
                            }
                        });
                    }}
                />
            ));
        })()}

        {(() => {
            if (scale !== null) {
                return (
                <div id={css.cmds}
                    style={{
                        transform: `scale(${1 / scale!.w}, ${1 / scale!.h})`,
                        transformOrigin: 'top left',
                    }}
                >
                    <button onClick={() => {
                        setIcons(icons.concat([{
                            icon: null,
                            left: main.current!.clientWidth / 2,
                            top: main.current!.clientHeight / 2,
                            command: '',
                            hover: '',
                        }]));
                    }}>新增 base icon</button>
                    <button onClick={() => {
                        setIcons(icons.concat([{
                            width: 350,
                            height: 100,
                            left: (main.current!.clientWidth - 350) / 2,
                            top: (main.current!.clientHeight - 100) / 2,
                            command: '',
                            hover: '',
                        }]));
                    }}>新增 rect icon</button>
                    <button onClick={() => {
                        // pathname = '/' or '/0000'
                        const book = new URL(document.location.href).pathname.split('/')[1];
                        api.setting.set(book === '' ? null : book, page, icons);
                    }}>儲存</button>
                </div>);
            }
        })()}

        {(() => {
            if (editing !== null) {
                return (
                <div
                    style={{
                        transform: `scale(${1 / scale!.w}, ${1 / scale!.h})`,
                        transformOrigin: 'top left',
                    }}
                >
                    <Editor
                        icon={icons[editing]}
                        onChange={icon => setIcons(amend(icons, editing, icon))}
                        onDelete={() => {
                            setIcons(remove(editing, 1, icons));
                            setEditing(null);
                        }}
                        onDone={() => setEditing(null)}
                    />
                </div>);
            }
        })()}
    </div>);
}

async function fetchIcons(page: string): Promise<Icon[]> {
    const res = await fetch(settingFile(page));
    if (res.status === 200) {
        const icons = await res.json();
        assert(icons instanceof Array && icons.every((i): i is Icon => Icon.isa(i)), '補充資料設定檔格式錯誤');
        return icons;
    } else {
        return [];
    }
}

function settingFile(page: string) {
    // page could be '04,05/index.dom.html' or '06,07.jpg'
    const level1 = page.split('/')[0];      // '04,05' or '06,07.jpg'
    const name = level1.replace(/\..*/, '');   // '04,05' or '06,07'
    return `${setting.bookDir}/pages/${name}.json`;
}
